import { useState } from "react";
import { CheckboxControl, ErrorControl, Label, TextInput } from "@osago/ui";
import { AccordionSection } from "@/features/configurator/components/AccordionSection";
import { getEntryPoint, UTM_FIELDS, RESERVED_UTM_SOURCE } from "@/shared/config/referral";
import { CHAN_MAX, PARTNER_ID_MAX } from "@/shared/lib/partnerParams";
import { useReferral } from "../model/referralContext";
import { EntryPointField } from "./EntryPointField";
import { CompaniesField } from "@/shared/ui/CompaniesField";

export function ReferralSettingsPanel() {
  const {
    partnerId,
    setPartnerId,
    partnerIdLocked,
    chan,
    setChan,
    b,
    setB,
    entryPoint,
    setEntryPoint,
    companies,
    companiesCleared,
    setCompanies,
    hideHeader,
    setHideHeader,
    hideFooter,
    setHideFooter,
    noJivochat,
    setNoJivochat,
    utm,
    setUtm,
  } = useReferral();
  const [touched, setTouched] = useState(false);

  const entry = getEntryPoint(entryPoint);
  const partnerError = touched && !partnerId.trim() ? "Укажите партнёрский ID" : "";
  const utmSourceReserved = utm.utm_source === RESERVED_UTM_SOURCE;

  return (
    <div className="flex flex-col gap-4">
      <AccordionSection title="Партнёр" defaultOpen>
        <TextInput
          label="Партнёрский ID"
          required
          value={partnerId}
          onChange={(v) => {
            setTouched(true);
            setPartnerId(v);
          }}
          placeholder="например, 1024"
          maxLength={PARTNER_ID_MAX}
          readOnly={partnerIdLocked}
          hasError={Boolean(partnerError)}
          rightSlot={
            partnerError ? <ErrorControl isOK={false} errorMessage={partnerError} /> : undefined
          }
        />
        <Label variant="hint" className="mt-1.5 block">
          Выдаётся при регистрации в партнёрской программе
        </Label>
        <div className="mt-3">
          <TextInput
            label="Chan ID"
            value={chan}
            onChange={setChan}
            placeholder="например, email"
            maxLength={CHAN_MAX}
          />
          <Label variant="hint" className="mt-1.5 block">
            Метка канала или площадки — для статистики в кабинете партнёра
          </Label>
        </div>
      </AccordionSection>

      <AccordionSection title="Страница входа" defaultOpen>
        <EntryPointField value={entryPoint} onChange={setEntryPoint} />
      </AccordionSection>

      {entry.supportsCompanies && (
        <AccordionSection title="Страховые компании">
          <Label variant="hint" className="mb-2.5 block">
            По умолчанию клиент видит предложения всех доступных СК. Отметьте компании, чтобы
            ограничить список.
          </Label>
          <CompaniesField
            selected={companies}
            cleared={companiesCleared}
            onChange={setCompanies}
          />
        </AccordionSection>
      )}

      <AccordionSection title="UTM-метки">
        <Label variant="hint" className="mb-2.5 block">
          Учитываются в веб-аналитике (Яндекс.Метрика). На атрибуцию продажи не влияют.
        </Label>
        <div className="flex flex-col gap-3">
          {UTM_FIELDS.map((field) => (
            <TextInput
              key={field.key}
              label={field.key}
              value={utm[field.key] ?? ""}
              onChange={(v) => setUtm(field.key, v)}
              placeholder={field.placeholder}
            />
          ))}
        </div>
        {utmSourceReserved && (
          <Label variant="error" className="mt-2 block">
            Значение «{RESERVED_UTM_SOURCE}» зарезервировано под режим виджета — в реферальной
            ссылке его использовать нельзя.
          </Label>
        )}
      </AccordionSection>

      <AccordionSection title="Отображение">
        <div className="flex flex-col gap-2.5">
          <CheckboxControl
            id="referral-hide-header"
            checked={hideHeader}
            onChange={setHideHeader}
            label="Скрыть шапку сайта"
          />
          <CheckboxControl
            id="referral-no-jivochat"
            checked={noJivochat}
            onChange={setNoJivochat}
            label="Отключить онлайн-чат"
          />
          <CheckboxControl
            id="referral-hide-footer"
            checked={hideFooter}
            onChange={setHideFooter}
            label="Скрыть футер сайта"
          />
        </div>
      </AccordionSection>

      <AccordionSection title="Дополнительно">
        <TextInput
          label="ID баннера"
          value={b}
          onChange={setB}
          placeholder="например, banner7"
          maxLength={PARTNER_ID_MAX}
        />
        <Label variant="hint" className="mt-1.5 block">
          Идентификатор баннера или креатива из кабинета партнёра — чтобы сравнивать
          эффективность размещений
        </Label>
      </AccordionSection>
    </div>
  );
}
